import type {
  AckRecordInput,
  AgentKnowledgeUsage,
  AgentUsingBase,
  ApiIssue,
  AudienceInput,
  ConnectorFolder,
  CoverageReport,
  IngestionPolicy,
  KnowledgeBase,
  KnowledgeBaseAccessView,
  KnowledgeBaseList,
  KnowledgeBaseView,
  KnowledgeChunk,
  KnowledgeDocument,
  LendingRequest,
  OwnerRef,
  SearchResponse,
  ShareOutcome
} from '@ralph/contracts'
import { ApiRequestError, apiGet, apiSend, getCsrfToken } from './client.ts'

/**
 * Knowledge bases (PRD-26): one kind of base, owned by a person or a group, used by the people it
 * is shared with and maintained by its owner, its owning group's managers and administrators.
 *
 *   GET    /api/knowledge-bases?view=maintain|use|all        → { items, allowance }
 *   POST   /api/knowledge-bases                               → 201 KnowledgeBase
 *   GET    /api/knowledge-bases/:id                           → KnowledgeBase
 *   PATCH  /api/knowledge-bases/:id                           → KnowledgeBase
 *   DELETE /api/knowledge-bases/:id  { confirmName }          → { deleted, affectedAgents }
 *   POST   /api/knowledge-bases/:id/move { to }               → KnowledgeBase
 *   GET    /api/knowledge-bases/:id/documents                 → { items }
 *   POST   /api/knowledge-bases/:id/documents (multipart)     → IngestResult
 *   DELETE /api/knowledge-bases/:id/documents/:docId          → { deleted, leftOutOfFolder }
 *   POST   /api/knowledge-bases/:id/documents/:docId/reprocess
 *   POST   /api/knowledge-bases/:id/reprocess[?all=true]      → 202 { queued }
 *   GET    /api/knowledge-bases/:id/documents/:docId/chunks   → { items }
 *   POST   /api/knowledge-bases/:id/search                    → SearchResponse
 *   GET    /api/knowledge-bases/:id/access                    → KnowledgeBaseAccessView
 *   PUT    /api/knowledge-bases/:id/access                    → ShareOutcome
 *   GET    /api/knowledge-bases/:id/agents                    → { items: AgentUsingBase[] }
 *   POST   /api/knowledge-bases/:id/lending { agentId, ackText }
 *   DELETE /api/knowledge-bases/:id/lending/:agentId
 *   POST   /api/knowledge-bases/:id/lending-requests { agentId, note }
 *   GET · POST /api/lending-requests/:id · /decide · /withdraw
 *   GET    /api/knowledge-bases/:id/folders                   → { items }
 *   POST   /api/maker/agents/:agentId/coverage { audience }   → CoverageReport
 *
 * Every failure arrives in the canonical envelope, as an `ApiRequestError`; the recognisers at the
 * bottom are the only places that read a `code`.
 */

const basePath = (baseId: string): string => `/api/knowledge-bases/${encodeURIComponent(baseId)}`

// ---------------------------------------------------------------------------
// Bases
// ---------------------------------------------------------------------------

export const fetchKnowledgeBases = (view: KnowledgeBaseView = 'maintain'): Promise<KnowledgeBaseList> =>
  apiGet<KnowledgeBaseList>(`/api/knowledge-bases?view=${view}`)

export const fetchKnowledgeBase = (baseId: string): Promise<KnowledgeBase> => apiGet<KnowledgeBase>(basePath(baseId))

/**
 * The server names the owner of something NEW as `me` or `{ group, groupId }` — a base is never
 * created on another person's behalf; an administrator who means to give one away creates it and
 * then moves it. A `user` owner here is therefore always the caller.
 */
const createOwnerOf = (owner: OwnerRef | undefined) =>
  owner?.kind === 'group' ? { kind: 'group' as const, groupId: owner.id } : { kind: 'me' as const }

export const createKnowledgeBase = (input: {
  name: string
  description: string
  owner?: OwnerRef
}): Promise<KnowledgeBase> =>
  apiSend<KnowledgeBase>('/api/knowledge-bases', 'POST', {
    name: input.name,
    description: input.description,
    owner: createOwnerOf(input.owner)
  })

export type KnowledgeBasePatch = {
  name?: string
  description?: string
  freshnessSlaHours?: number
  ingestionPolicy?: IngestionPolicy
}

export const updateKnowledgeBase = (baseId: string, patch: KnowledgeBasePatch): Promise<KnowledgeBase> =>
  apiSend<KnowledgeBase>(basePath(baseId), 'PATCH', patch)

export type DeleteBaseResult = { deleted: true; affectedAgents: Array<{ agentId: string; displayName: string }> }

/** Refused with `confirm_name_required` until the name is typed back. */
export const deleteKnowledgeBase = (baseId: string, confirmName?: string): Promise<DeleteBaseResult> =>
  apiSend<DeleteBaseResult>(basePath(baseId), 'DELETE', confirmName === undefined ? {} : { confirmName })

/** Moving never changes who can use it (D-9); the destination's limits apply (D-41). */
export const moveKnowledgeBase = (baseId: string, to: OwnerRef): Promise<KnowledgeBase> =>
  apiSend<KnowledgeBase>(`${basePath(baseId)}/move`, 'POST', { to })

// ---------------------------------------------------------------------------
// Documents
// ---------------------------------------------------------------------------

export const fetchKnowledgeBaseDocuments = (baseId: string): Promise<{ items: KnowledgeDocument[] }> =>
  apiGet<{ items: KnowledgeDocument[] }>(`${basePath(baseId)}/documents`)

export const fetchDocumentChunks = (baseId: string, documentId: string): Promise<{ items: KnowledgeChunk[] }> =>
  apiGet<{ items: KnowledgeChunk[] }>(`${basePath(baseId)}/documents/${encodeURIComponent(documentId)}/chunks`)

/**
 * What an upload answers with. `status` is the file's state when the server answered: read
 * already, or still being read on the queue — and a file that could not be read is still a
 * success whose row says so, which is why it is not an error here.
 */
export type DocumentUploadResult = {
  documentId: string
  status: KnowledgeDocument['status']
  chunkCount: number
  /** The same file was already in this base; nothing new was stored. */
  reused: boolean
  detail?: string | null
}

/**
 * A request whose answer is a reading verdict: an upload, or reading a stored copy again.
 *
 * Not routed through `api()`. A file that was read and could not be used — failed, or set aside
 * by the malware scan — answers 422 with the verdict as its body, and a verdict is an answer, not
 * a failed request: routed through `api()` it surfaced as a bare "Unprocessable Entity" and the
 * row that says what happened was never looked at. Only a body without a document is a refusal.
 */
const sendForVerdict = async (path: string, body?: FormData): Promise<DocumentUploadResult> => {
  const headers = new Headers()
  const csrf = getCsrfToken()
  if (csrf) headers.set('x-ralph-csrf', csrf)

  // A multipart body must not have its content-type set by hand, or the boundary is lost.
  const response = await fetch(path, {
    method: 'POST',
    headers,
    ...(body ? { body } : {}),
    credentials: 'same-origin'
  })

  const payload = (await response.json().catch(() => null)) as
    | (Partial<DocumentUploadResult> & { issues?: ApiIssue[]; requestId?: string })
    | null

  const verdict = typeof payload?.documentId === 'string' && payload.documentId !== ''
  if (!response.ok && !(response.status === 422 && verdict)) {
    throw new ApiRequestError(
      response.status,
      payload?.issues && payload.issues.length > 0
        ? payload.issues
        : [
            {
              code: response.status === 413 ? 'payload_too_large' : 'upload_failed',
              message: response.statusText || 'The file was not added.',
              field: null,
              retryable: response.status >= 500
            }
          ],
      payload?.requestId ?? ''
    )
  }

  return {
    documentId: payload?.documentId ?? '',
    status: payload?.status ?? 'failed',
    chunkCount: payload?.chunkCount ?? 0,
    reused: payload?.reused ?? false,
    detail: payload?.detail ?? null
  }
}

/** One file, as multipart, into a base the caller maintains and within its limits (D-41). */
export const uploadKnowledgeBaseDocument = (baseId: string, file: File): Promise<DocumentUploadResult> => {
  const form = new FormData()
  form.append('file', file)
  return sendForVerdict(`${basePath(baseId)}/documents`, form)
}

export const deleteKnowledgeBaseDocument = (
  baseId: string,
  documentId: string
): Promise<{ deleted: true; leftOutOfFolder: boolean }> =>
  apiSend<{ deleted: true; leftOutOfFolder: boolean }>(
    `${basePath(baseId)}/documents/${encodeURIComponent(documentId)}`,
    'DELETE'
  )

/** Reads the stored copy again with the pipeline now deployed (PRD-24 ING-013); the verdict is an answer. */
export const reprocessDocument = (baseId: string, documentId: string): Promise<DocumentUploadResult> =>
  sendForVerdict(`${basePath(baseId)}/documents/${encodeURIComponent(documentId)}/reprocess`)

export const reprocessBase = (baseId: string, all = false): Promise<{ queued: number }> =>
  apiSend<{ queued: number }>(`${basePath(baseId)}/reprocess${all ? '?all=true' : ''}`, 'POST')

/** The same search an agent runs, over one base (D-45). Ranks and arms are shown to maintainers. */
export const searchKnowledgeBase = (
  baseId: string,
  input: { query: string; limit?: number; strategy?: 'hybrid' | 'semantic' | 'lexical' }
): Promise<SearchResponse> => apiSend<SearchResponse>(`${basePath(baseId)}/search`, 'POST', input)

// ---------------------------------------------------------------------------
// Who can use it (maintainers)
// ---------------------------------------------------------------------------

export const fetchBaseAccess = (baseId: string): Promise<KnowledgeBaseAccessView> =>
  apiGet<KnowledgeBaseAccessView>(`${basePath(baseId)}/access`)

/**
 * Changes who can use it. What the maintainer may do alone goes live now (`applied`); what needs
 * an administrator becomes one sharing request (`request`) — D-13, D-40. `ackText` is the exact
 * sentence they read, stored with whichever happened; `ifVersion` refuses an edit made over a
 * colleague's.
 */
export const changeBaseAccess = (
  baseId: string,
  input: { access: AudienceInput; ackText: string; ifVersion?: number } & AckRecordInput
): Promise<ShareOutcome> => apiSend<ShareOutcome>(`${basePath(baseId)}/access`, 'PUT', input)

// ---------------------------------------------------------------------------
// Agents that use it, and letting one answer for everyone it reaches (D-16, D-17)
// ---------------------------------------------------------------------------

export const fetchAgentsUsingBase = (baseId: string): Promise<{ items: AgentUsingBase[] }> =>
  apiGet<{ items: AgentUsingBase[] }>(`${basePath(baseId)}/agents`)

/**
 * `ackText` is the decision screen's sentence, stored verbatim as the evidence; the `ack*` fields
 * beside it are the record that can be read back in either language (PRD-27 D-7).
 */
export const lendToAgent = (
  baseId: string,
  input: { agentId: string; ackText: string } & AckRecordInput
): Promise<{ lent: true }> => apiSend<{ lent: true }>(`${basePath(baseId)}/lending`, 'POST', input)

export const stopLending = (baseId: string, agentId: string): Promise<{ stopped: boolean }> =>
  apiSend<{ stopped: boolean }>(`${basePath(baseId)}/lending/${encodeURIComponent(agentId)}`, 'DELETE')

/** "Ask its managers" — from the builder (a maker) or the agent's page (a reader it reaches). */
export const askToLend = (baseId: string, agentId: string, note = ''): Promise<LendingRequest> =>
  apiSend<LendingRequest>(`${basePath(baseId)}/lending-requests`, 'POST', { agentId, note })

export const fetchLendingRequest = (requestId: string): Promise<LendingRequest> =>
  apiGet<LendingRequest>(`/api/lending-requests/${encodeURIComponent(requestId)}`)

export const decideLendingRequest = (
  requestId: string,
  input: { decision: 'approved' | 'declined'; note?: string; ackText?: string } & AckRecordInput
): Promise<LendingRequest> =>
  apiSend<LendingRequest>(`/api/lending-requests/${encodeURIComponent(requestId)}/decide`, 'POST', {
    note: '',
    ackText: '',
    ...input
  })

export const withdrawLendingRequest = (requestId: string): Promise<LendingRequest> =>
  apiSend<LendingRequest>(`/api/lending-requests/${encodeURIComponent(requestId)}/withdraw`, 'POST')

// ---------------------------------------------------------------------------
// Connected folders feeding a base, and an agent's knowledge
// ---------------------------------------------------------------------------

export const fetchBaseFolders = (baseId: string): Promise<{ items: ConnectorFolder[] }> =>
  apiGet<{ items: ConnectorFolder[] }>(`${basePath(baseId)}/folders`)

export type CoverageBaseEntry = CoverageReport['bases'][number]

/** Coverage against the audience being CHOSEN, not the one last published (GRP-045). */
export const fetchAgentCoverage = (agentId: string, audience: AudienceInput): Promise<CoverageReport> =>
  apiSend<CoverageReport>(`/api/maker/agents/${encodeURIComponent(agentId)}/coverage`, 'POST', { audience })

export const fetchAgentKnowledgeUsage = (agentId: string, days: number): Promise<AgentKnowledgeUsage> =>
  apiGet<AgentKnowledgeUsage>(`/api/maker/agents/${encodeURIComponent(agentId)}/knowledge-usage?days=${days}`)

// ---------------------------------------------------------------------------
// Refusals the interface has a designed state for
// ---------------------------------------------------------------------------

/** "Type the name to confirm" — the server decides when it applies; the dialog discovers it. */
export const isConfirmNameRequired = (error: unknown): boolean => {
  if (!(error instanceof ApiRequestError) || error.status >= 500) return false
  return error.issues.some((issue) => issue.code === 'confirm_name_required' || issue.field === 'confirmName')
}

/** Somebody changed who can use it while this maintainer was editing (If-Match on access). */
export const isStaleEdit = (error: unknown): boolean =>
  error instanceof ApiRequestError && error.code === 'conflict' && error.issues[0]?.field === 'ifVersion'

/** One file over the absolute upload limit — different from a base that is full. */
export const isFileTooLarge = (error: unknown): boolean =>
  error instanceof ApiRequestError && (error.status === 413 || error.code === 'payload_too_large')

/** A rate limit is a "later", not a "no". */
export const isRateLimited = (error: unknown): boolean =>
  error instanceof ApiRequestError && (error.status === 429 || error.code.includes('rate_limit'))

/** A limit the owner can act on: a person's or a group's, per base or per owner (D-41). */
export const isQuotaRefusal = (error: unknown): boolean =>
  error instanceof ApiRequestError && (error.code.includes('quota') || error.code.includes('limit_reached'))

/*
 * There was a `refusalMessage()` here, and it is gone on purpose (PRD-27 D-4, F-1).
 *
 * It returned `issues[0].message` — the server's English — and it was a documented contract to
 * RENDER that English, which is why four surfaces did, two of them appended after a localized
 * fragment. No amount of care at those call sites could fix that: the sentence only exists in
 * one language until the wire carries something else, and now it does. `describeApiError` and
 * `describeIssue` render an issue's `messageKey` from the reader's own catalogue and keep the
 * English only as the fallback for a key this build has not learned yet.
 *
 * The recognisers above stay, because they answer a different question: `code` is the machine
 * failure class, branching on it is not branching on prose, and a surface that offers "choose a
 * smaller file" has to know which refusal it got. Branch on the code or on the key — never on
 * the words (D-6).
 */
